import { Ionicons } from '@expo/vector-icons';
import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { palette, useThemePalette } from '@/config/theme';
import { compactForm } from '@/features/predictions/model';
import type { FixtureWithRefs } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import type { TopPlayerEntry, Tables } from '@/lib/supabase/types';

const FORM_TONE: Record<string, string> = { W: palette.win, D: palette.draw, L: palette.loss };

function FormPips({ form }: { form: string | null }) {
  const palette = useThemePalette();
  const compact = compactForm(form);
  if (!compact) return <AppText variant="micro" className="text-muted">-</AppText>;
  return (
    <View className="flex-row gap-1">
      {compact.split('-').map((letter, index) => {
        const tone = FORM_TONE[letter] ?? palette.muted;
        return (
          <View
            key={index}
            className="h-4 w-4 items-center justify-center rounded-full"
            style={{ backgroundColor: `${tone}26` }}
          >
            <AppText weight="bold" style={{ fontSize: 9, color: tone }}>
              {letter}
            </AppText>
          </View>
        );
      })}
    </View>
  );
}

/** One comparison line: home value · centred metric label · away value. */
function Row({
  label,
  home,
  away,
}: {
  label: string;
  home: React.ReactNode;
  away: React.ReactNode;
}) {
  return (
    <View className="flex-row items-center px-4 py-3">
      <View className="flex-1 flex-row justify-start">{home}</View>
      <AppText variant="micro" weight="bold" className="w-24 text-center uppercase tracking-wide text-muted">
        {label}
      </AppText>
      <View className="flex-1 flex-row justify-end">{away}</View>
    </View>
  );
}

function PlayerCell({ entry, align }: { entry: TopPlayerEntry | null; align: 'start' | 'end' }) {
  const { numeralOptions } = useLocale();
  if (!entry) return <AppText variant="micro" className="text-muted">-</AppText>;
  const name = entry.known_as || entry.name;
  const goals = (
    <AppText variant="micro" weight="bold" tabular className="text-volt-bright">
      {formatNumber(entry.goals, numeralOptions)}⚽
    </AppText>
  );
  const avatar = <PlayerAvatar uri={entry.photo_url} name={name} size={30} />;
  const label = (
    <View className={align === 'end' ? 'items-end' : 'items-start'} style={{ maxWidth: 96 }}>
      <AppText variant="micro" weight="semibold" className="text-ink" numberOfLines={1}>
        {name}
      </AppText>
      {goals}
    </View>
  );
  return (
    <View className="flex-row items-center gap-2">
      {align === 'end' ? label : avatar}
      {align === 'end' ? avatar : label}
    </View>
  );
}

/**
 * Pre-match comparison of the two nations (replaces H2H). Top scorer + most
 * dangerous (goals+assists) from the competition's aggregated stats, plus form
 * and group standing - the "history/form story" before kick-off.
 */
export function MatchComparison({
  fixture,
  homeRow,
  awayRow,
  topPlayers,
}: {
  fixture: FixtureWithRefs;
  homeRow: Tables<'standings'> | null;
  awayRow: Tables<'standings'> | null;
  topPlayers: TopPlayerEntry[];
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();

  const { homeScorer, awayScorer, homeKey, awayKey } = useMemo(() => {
    const forTeam = (teamName: string | undefined) =>
      teamName ? topPlayers.filter((p) => p.team_name === teamName) : [];
    const home = forTeam(fixture.home_team?.name);
    const away = forTeam(fixture.away_team?.name);
    const topScorer = (list: TopPlayerEntry[]) =>
      [...list].sort((a, b) => b.goals - a.goals)[0] ?? null;
    const keyPlayer = (list: TopPlayerEntry[]) =>
      [...list].sort((a, b) => b.goals + b.assists - (a.goals + a.assists))[0] ?? null;
    return {
      homeScorer: topScorer(home),
      awayScorer: topScorer(away),
      homeKey: keyPlayer(home),
      awayKey: keyPlayer(away),
    };
  }, [topPlayers, fixture.home_team, fixture.away_team]);

  const rank = (row: Tables<'standings'> | null) =>
    row ? (
      <AppText variant="caption" weight="bold" tabular className="text-ink">
        #{formatNumber(row.rank, numeralOptions)} · {formatNumber(row.points, numeralOptions)}{' '}
        {t('common.pts')}
      </AppText>
    ) : (
      <AppText variant="micro" className="text-muted">-</AppText>
    );

  // Only show once there's something meaningful to compare - a scorer on either
  // side. Pre-tournament (no goals yet) the card stays hidden instead of showing
  // a row of em-dashes.
  const hasScorers = Boolean(homeScorer || awayScorer);
  if (!hasScorers) return null;

  return (
    <Card className="mx-4 mb-4 overflow-hidden">
      <View className="flex-row items-center gap-2 border-b border-line px-4 py-3">
        <Ionicons name="git-compare-outline" size={14} color={palette.volt} />
        <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-ink">
          {t('match.comparison')}
        </AppText>
      </View>
      {hasScorers ? (
        <View className="border-b border-line">
          <Row
            label={t('match.topScorer')}
            home={<PlayerCell entry={homeScorer} align="start" />}
            away={<PlayerCell entry={awayScorer} align="end" />}
          />
        </View>
      ) : null}
      {homeKey || awayKey ? (
        <View className="border-b border-line">
          <Row
            label={t('match.keyPlayer')}
            home={<PlayerCell entry={homeKey} align="start" />}
            away={<PlayerCell entry={awayKey} align="end" />}
          />
        </View>
      ) : null}
      <View className="border-b border-line">
        <Row label={t('match.recentForm')} home={<FormPips form={homeRow?.form ?? null} />} away={<FormPips form={awayRow?.form ?? null} />} />
      </View>
      <Row label={t('match.standing')} home={rank(homeRow)} away={rank(awayRow)} />
    </Card>
  );
}
